<template>
  <div style="position: absolute; left: -9999px; top: -9999px;">
    <table v-for="(entry, idx) in tableEntries" :key="idx" :id="`catalogTable-${idx}`" :style="tableStyle">
      <thead>
        <tr><th colspan="2" :style="titleStyle">{{ entry.item.name }}</th></tr>
      </thead>
      <tbody>
        <tr v-for="(row, rIdx) in entry.rows" :key="rIdx">
          <td :style="valueStyle"><template v-if="row.col1"><b>{{ row.col1.label }}: </b>{{ printValue(entry.item, row.col1) }}</template></td>
          <td :style="valueStyle"><template v-if="row.col2"><b>{{ row.col2.label }}: </b>{{ printValue(entry.item, row.col2) }}</template></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
<script>
import { resolveAttributeValue, effectiveManualValue, htmlToDocxBlob } from './declarationsService.js'
import { initPdf, initPdfLandscape, setPageNumber } from '@/modules/all-reports/pdf/config.js'
import state from '@/state'

export default {
  name: 'DeclarationCatalogPrintTable',
  props: {
    entries: { type: Array, required: true },
    settings: { type: Object, required: true },
    documentTitle: { type: String, default: 'Knjiga deklaracija' }
  },
  computed: {
    // Used only for the DOCX export (html-docx-js reads this table's inline
    // CSS directly). PDF export no longer goes through this HTML/CSS at
    // all — jspdf-autotable's `useCss` mode did not reliably pick up a
    // border defined on the outer <table> tag, so `generate()` below styles
    // the PDF table via autoTable's own JS options and draws the "card"
    // border directly with doc.rect()/doc.roundedRect() instead.
    tableStyle () {
      const border = this.settings.border ? '1.5pt solid #263238' : 'none'
      const borderRadius = (this.settings.border && this.settings.border_style === 'rounded') ? '8pt' : '0pt'
      return `border: ${border}; border-radius: ${borderRadius}; border-collapse: collapse; margin-bottom: 14pt; width: 100%;`
    },
    titleStyle () {
      return `font-size: ${this.settings.font_size + 3}pt; font-weight: bold; text-align: center; padding: 6pt 8pt; background-color: #F1F5F9; border-bottom: 0.75pt solid #263238;`
    },
    valueStyle () {
      return `font-size: ${this.settings.font_size}pt; padding: 4pt 8pt; vertical-align: top;`
    },
    tableEntries () {
      return this.entries.map(entry => {
        const visibleFor = column => entry.fields
          .filter(f => f.column === column && f.printaj && !f.removed && this.printValue(entry.item, f) !== '')
          .sort((a, b) => a.order - b.order)
        const col1 = visibleFor(1)
        const col2 = visibleFor(2)
        const rowCount = Math.max(col1.length, col2.length)
        const rows = []
        for (let i = 0; i < rowCount; i++) {
          rows.push({ col1: col1[i] || null, col2: col2[i] || null })
        }
        return { item: entry.item, rows }
      })
    },
    companyName () {
      return state.getCurrentCompany().name
    }
  },
  methods: {
    printValue (item, field) {
      return field.type === 'attribute' ? resolveAttributeValue(item, field.attribute_field) : effectiveManualValue(field)
    },
    // Splits "Label: Value" into draw-ready lines that fit within
    // `cellWidth` — the value's start goes beside the label if there's
    // room, and any remainder wraps onto full-width lines below (starting
    // at the cell's own left edge), matching how DeclarationLabelPrintTable
    // wraps naljepnice text. Used identically for both height calculation
    // (didParseCell) and drawing (didDrawCell) so the two never disagree.
    buildFieldLines (doc, label, value, cellWidth, fontSize) {
      doc.setFontSize(fontSize)
      doc.setFont('Roboto-Bold', 'bold')
      const labelText = `${label}: `
      const labelWidth = doc.getTextWidth(labelText)
      doc.setFont('Roboto-Regular', 'normal')
      const besideWidth = cellWidth - labelWidth
      const valueText = String(value)
      const lines = []

      if (besideWidth >= cellWidth * 0.3) {
        const narrowLines = doc.splitTextToSize(valueText, besideWidth)
        const firstLine = narrowLines[0] || ''
        lines.push({ label: labelText, labelWidth, value: firstLine })
        const remainder = valueText.slice(firstLine.length).trim()
        if (remainder) {
          doc.splitTextToSize(remainder, cellWidth).forEach(text => lines.push({ label: null, labelWidth: 0, value: text }))
        }
      } else {
        lines.push({ label: labelText, labelWidth, value: '' })
        doc.splitTextToSize(valueText, cellWidth).forEach(text => lines.push({ label: null, labelWidth: 0, value: text }))
      }

      return lines
    },
    async generate () {
      const doc = this.settings.orientation === 'landscape' ? await initPdfLandscape() : await initPdf()
      const pageWidth = doc.internal.pageSize.getWidth()
      const marginLeft = 40
      const marginRight = 40
      const contentWidth = pageWidth - marginLeft - marginRight
      const borderColor = [38, 50, 56]

      doc.setFont('Roboto-Bold', 'bold')
      doc.setFontSize(16)
      doc.text(this.companyName, pageWidth / 2, 30, { align: 'center' })
      doc.setFontSize(12)
      doc.text(this.documentTitle, pageWidth / 2, 48, { align: 'center' })
      doc.setFont('Roboto-Regular', 'normal')

      let y = 70
      let countOnPage = 0
      const perPage = this.settings.one_per_page ? 1 : Math.max(1, this.settings.per_page || 1)

      // Exact 50/50 split when the template uses both columns — fixed
      // widths (not autoTable's own auto-sizing) so column 1's text can
      // never run into column 2's space.
      const cellPaddingH = 8
      const halfWidth = contentWidth / 2
      const halfTextWidth = halfWidth - cellPaddingH * 2
      const lineHeightFactor = doc.getLineHeightFactor()

      for (let i = 0; i < this.tableEntries.length; i++) {
        const startY = y
        const startPage = doc.internal.getCurrentPageInfo().pageNumber
        const wrapLineHeight = this.settings.font_size / doc.internal.scaleFactor * lineHeightFactor

        // Styled entirely via autoTable's own JS options, not CSS/useCss —
        // useCss did not reliably pick up border on/off from the outer
        // <table> tag; explicit styles here are guaranteed to apply.
        // `pageBreak: 'avoid'` keeps one entry's rows together — if it
        // doesn't fit in the remaining space, autoTable moves the WHOLE
        // entry to a fresh page (starting at margin.top, matched to our
        // own manual addPage()'s y=10 below) rather than splitting rows
        // mid-entry across two pages.
        doc.autoTable({
          html: `#catalogTable-${i}`,
          startY,
          margin: { top: 10, left: marginLeft, right: marginRight, bottom: 10 },
          pageBreak: 'avoid',
          theme: 'plain',
          tableWidth: contentWidth,
          styles: {
            font: 'Roboto-Regular',
            fontSize: this.settings.font_size,
            cellPadding: { top: 4, right: cellPaddingH, bottom: 4, left: cellPaddingH },
            valign: 'top'
          },
          headStyles: {
            font: 'Roboto-Bold',
            fontStyle: 'bold',
            fontSize: this.settings.font_size + 3,
            halign: 'center',
            fillColor: [241, 245, 249],
            textColor: borderColor,
            cellPadding: { top: 6, right: cellPaddingH, bottom: 6, left: cellPaddingH }
          },
          columnStyles: {
            0: { cellWidth: halfWidth },
            1: { cellWidth: halfWidth }
          },
          // Body cells hold "Label: Value" as one plain string by default —
          // here we instead give each cell a `cell.text` array whose length
          // matches EXACTLY how many lines `buildFieldLines` will draw, so
          // autoTable reserves the correct row height, drawn invisible
          // (white); didDrawCell then paints the real wrapped text on top
          // with the label bold and the value regular, wrapped to fit
          // `halfTextWidth` the same way naljepnice text wraps.
          didParseCell: (data) => {
            if (data.section !== 'body') return
            data.cell.styles.textColor = [255, 255, 255]
            const row = this.tableEntries[i].rows[data.row.index]
            const pair = data.column.index === 0 ? row.col1 : row.col2
            if (!pair) { data.cell.text = ['']; return }
            const value = this.printValue(this.tableEntries[i].item, pair)
            const lineCount = this.buildFieldLines(doc, pair.label, value, halfTextWidth, this.settings.font_size).length
            data.cell.text = new Array(lineCount).fill('')
          },
          didDrawCell: (data) => {
            if (data.section !== 'body') return
            const row = this.tableEntries[i].rows[data.row.index]
            const pair = data.column.index === 0 ? row.col1 : row.col2
            if (!pair) return
            const value = this.printValue(this.tableEntries[i].item, pair)
            const lines = this.buildFieldLines(doc, pair.label, value, halfTextWidth, this.settings.font_size)
            const x = data.cell.x + data.cell.padding('left')
            const baseY = data.cell.y + data.cell.padding('top') + this.settings.font_size * 0.8
            doc.setFontSize(this.settings.font_size)
            doc.setTextColor(...borderColor)
            lines.forEach((line, idx) => {
              const lineY = baseY + idx * wrapLineHeight
              if (line.label) {
                doc.setFont('Roboto-Bold', 'bold')
                doc.text(line.label, x, lineY)
              }
              doc.setFont('Roboto-Regular', 'normal')
              doc.text(line.value, x + line.labelWidth, lineY)
            })
          }
        })
        doc.setTextColor(0, 0, 0)

        const endPage = doc.internal.getCurrentPageInfo().pageNumber
        const endY = doc.lastAutoTable.finalY

        if (this.settings.border) {
          // autoTable may have moved this entry to a new page on its own —
          // always draw the border on whichever page the table actually
          // finished on, starting from that page's own top if it moved,
          // not the stale startY belonging to the page it left behind.
          doc.setPage(endPage)
          const boxStartY = endPage === startPage ? startY : 10
          doc.setDrawColor(...borderColor)
          doc.setLineWidth(0.75)
          if (this.settings.border_style === 'rounded') {
            doc.roundedRect(marginLeft, boxStartY, contentWidth, endY - boxStartY, 4, 4)
          } else {
            doc.rect(marginLeft, boxStartY, contentWidth, endY - boxStartY)
          }
        }

        y = endY + 16
        countOnPage++

        const isLast = i === this.tableEntries.length - 1
        if (countOnPage >= perPage && !isLast) {
          doc.addPage()
          y = 10
          countOnPage = 0
        }
      }

      await setPageNumber(doc)
      return doc.output('datauristring').split(',')[1]
    },
    generateDocx () {
      const headerHtml = '<div style="text-align:center; margin-bottom: 16pt;">' +
        `<div style="font-size: 16pt; font-weight: bold;">${this.companyName}</div>` +
        `<div style="font-size: 12pt;">${this.documentTitle}</div>` +
        '</div>'
      const tablesHtml = this.tableEntries.map((_, idx) => {
        const html = document.getElementById(`catalogTable-${idx}`).outerHTML
        const isLast = idx === this.tableEntries.length - 1
        return isLast ? html : `<div style="page-break-after: always;">${html}</div>`
      }).join('')
      return htmlToDocxBlob(headerHtml + tablesHtml)
    }
  }
}
</script>
